import { BaseTag } from 'module/ui/components/atoms/tag';
import { Heading01 } from 'module/ui/components/styles/typography';
import { Tag, Textarea } from '@rocketlane/ui';
import { Button } from '@rocketlane/ui';
import styled, { css } from 'styled-components';

export const SectionCard = styled.div<{
  $active?: boolean;
}>`
  border: 2px solid var(--field-border-hover);
  border-radius: var(--border-radius-small);
  background: var(--white-100);
  width: 100%;
  min-width: 652px;
  margin: var(--spacing-03) 0;
  border: 2px solid var(--blue-40);

  ${({ $active = false }) =>
    $active &&
    css`
      border-color: var(--blue-60);
    `};

  .ant-form-item {
    margin-bottom: 0;

    .ant-form-item-control-input {
      min-height: 0;

      textarea {
        margin-top: var(--spacing-02);
      }
    }
  }
`;

export const SectionHeadingContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  background-color: var(--scarlet-gray-10);
  border-radius: var(--border-radius-small);
  line-height: var(--line-height-01);
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-bold);
  color: var(--text-tertiary);
  align-items: center;
  border-radius: var(--border-radius-small);
`;

export const SectionTypeHeader = styled.div`
  gap: var(--spacing-03);
  display: flex;
  padding: var(--spacing-03);

  svg {
    color: var(--icon-tertiary);
  }
`;

export const SectionWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-05);
`;
export const SectionContent = styled.div`
  padding: var(--spacing-05) var(--spacing-05);
`;
export const SectionDivider = styled.div`
  height: 1px;
  width: 100%;
  background-color: var(--button-ghost-border);
  margin: var(--spacing-05) 0;
`;

export const SectionPreview = styled(SectionCard)<{
  isEditable?: boolean;
  isLoading?: boolean;
  isGenerated?: boolean;
  showBorder?: boolean;
  isActive?: boolean;
}>`
  padding: var(--spacing-05);
  background-color: white;
  color: var(--text-tertiary);
  cursor: ${({ isEditable = true }) =>
    isEditable ? 'pointer' : 'not-allowed'};
  border: ${({ isActive = false }) =>
    isActive ? '1px solid var(--blue-60)' : '2px solid transparent'};

  ${({ isLoading = false }) =>
    isLoading &&
    css`
      justify-content: center;
      align-items: center;
      display: flex;
      min-height: 140px;
    `}
  &:hover {
    border: 2px solid var(--blue-20);
    box-shadow: none;
  }
`;

/** TABLE / RECORD: SectionCard uses min-width: 652px; modals need shrink + inner horizontal scroll. */
export const FluidSectionCard = styled(SectionCard)`
  min-width: 0;
  max-width: 100%;
  box-sizing: border-box;

  ${SectionContent} {
    min-width: 0;
    max-width: 100%;
  }
`;

export const FluidSectionPreview = styled(SectionPreview)`
  min-width: 0;
  max-width: 100%;
  box-sizing: border-box;

  > div {
    min-width: 0;
    max-width: 100%;
    width: 100%;
  }
`;

export const GeneratedSectionPreview = styled(SectionPreview)`
  padding: 0;
  margin: 0;
  &:hover {
    border-color: transparent;
    box-shadow: none;
  }
`;

export const PreviewConfigurationItem = styled.div`
  display: flex;
  align-items: center;
  gap: var(--spacing-03);
  padding-top: var(--spacing-05);

  svg {
    color: var(--icon-tertiary);
  }
`;

export const DropdownTitle = styled.div`
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-bold);
  padding: var(--spacing-02) var(--spacing-05) var(--spacing-04);
`;

export const PreviewConfiguration = styled.div`
  color: var(--text-tertiary);
  font-size: var(--font-size-base);
  margin-top: var(--spacing-02);
  display: flex;
  gap: var(--spacing-07);
`;

export const SectionTitle = styled.div`
  ${Heading01};
  color: var(--text-primary);
`;

export const SectionDescription = styled.div`
  font-size: var(--font-size-base);
  margin-top: var(--spacing-02);
  color: var(--text-tertiary);
  font-weight: var(--font-weight-regular);
`;

export const InputGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-04);
`;

export const InputLabel = styled.label`
  font-weight: var(--font-weight-semi-bold);
  font-size: var(--font-size-lg);
  color: var(--text-primary);
`;

export const ConfigurationBar = styled.div<{ hasOptions?: boolean }>`
  display: flex;
  justify-content: ${({ hasOptions }) =>
    hasOptions ? 'space-between' : 'flex-end'};
  align-items: center;
  border-top: 1px solid transparent;
  /* padding-top: var(--spacing-05); */
`;

export const ConfigurationGroup = styled.div<{ isDisabled?: boolean }>`
  display: flex;
  gap: var(--spacing-05);
  color: ${(props) =>
    props.isDisabled ? 'var(--gray-50)' : 'var(--gray-100)'};
`;

export const ConfigurationText = styled.div`
  display: flex;
  align-items: center;
  gap: var(--spacing-03);
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-semi-bold);
  cursor: pointer;
`;

export const ActionBar = styled.div`
  display: flex;
  gap: var(--spacing-04);
  align-items: center;
`;

export const ThrashButton = styled(Button)`
  width: 32px;
  height: 32px;

  &:hover {
    color: var(--red-60) !important;
  }
  border: none;
  align-items: center;
  justify-content: center;
`;

export const PreviewButton = styled(Button)`
  width: 32px;
  height: 32px;
  border: none;
  align-items: center;
  justify-content: center;
`;

export const DoneButton = styled(Button)`
  gap: var(--spacing-03);
`;

export const TextAreaContainer = styled(Textarea)`
  height: 76px;
  resize: vertical;
`;

export const SectionTableItemWrapper = styled.div`
  display: flex;
  width: 100%;
  min-height: 32px;
  height: auto;
  border-bottom: 1px solid var(--gray-10);
`;

export const SectionTableItem = styled.div<{
  isSubSection?: boolean;
  isValue?: boolean;
}>`
  display: flex;
  flex: 1;
  flex-wrap: wrap;
  align-items: flex-start;
  padding: var(--spacing-02) var(--spacing-03);
  gap: var(--spacing-02);
  padding-left: ${({ isSubSection = false }) =>
    isSubSection ? 'var(--spacing-05)' : '0'};

  color: ${({ isValue = false }) =>
    isValue ? 'var(--text-primary)' : 'var(--text-tertiary)'};

  ${BaseTag} {
    height: 24px;
    margin: 2px;
  }
`;

export const SectionTableContainer = styled.div`
  margin-top: var(--spacing-04);
  flex-direction: column;
  gap: var(--spacing-01);
`;

export const CustomFilterTag = styled(Tag)`
  margin-left: var(--spacing-03);
`;
